import { PricingTableSchema, type PricingTier, PricingTierSchema } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { cn } from "@aurumiq/ui/lib/utils";
import { Plus, Save, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { usePermission } from "@/auth/use-permission";
import { SortableList } from "@/components/sortable-list";
import { usePutPricingTiers } from "./queries";
import { describe, emptyObject, type FieldSpec } from "./schema-form/introspect";
import { SchemaForm } from "./schema-form/schema-form";

const TIER_FIELDS: Record<string, FieldSpec> = (() => {
  const s = describe(PricingTierSchema);
  return s.kind === "object" ? s.fields : {};
})();

interface Row {
  key: string;
  raw: Record<string, unknown>;
}

function newTier(): Record<string, unknown> {
  return { ...emptyObject(TIER_FIELDS), code: "", name: "", features: [] };
}

export function PricingEditor({ initial }: { initial: PricingTier[] }) {
  const canWrite = usePermission("cms:write");
  const put = usePutPricingTiers();
  const [rows, setRows] = useState<Row[]>(() =>
    initial.map((t, i) => ({ key: `${t.code}#${i}`, raw: { ...t } })),
  );
  const [selected, setSelected] = useState<string | null>(rows[0]?.key ?? null);
  const [dirty, setDirty] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const results = useMemo(() => rows.map((r) => PricingTierSchema.safeParse(r.raw)), [rows]);
  const invalid = results.filter((r) => !r.success).length;
  const dupCodes = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of rows) {
      const c = String(r.raw.code ?? "");
      seen.set(c, (seen.get(c) ?? 0) + 1);
    }
    return [...seen.entries()].filter(([, n]) => n > 1).map(([c]) => c);
  }, [rows]);

  const update = (fn: (prev: Row[]) => Row[]) => {
    setRows(fn);
    setDirty(true);
  };
  const sel = rows.find((r) => r.key === selected) ?? null;
  const selIdx = rows.findIndex((r) => r.key === selected);
  const selResult = selIdx >= 0 ? results[selIdx] : undefined;

  const onSave = async (reason: string) => {
    const tiers = rows.map((r, i) => ({ ...r.raw, sort: i }));
    const parsed = PricingTableSchema.parse(tiers);
    await put.mutateAsync({ tiers: parsed, reason: reason || undefined });
    setDirty(false);
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {dirty ? <StatusTag variant="warn">UNSAVED</StatusTag> : null}
        {invalid ? <StatusTag variant="err">{invalid} INVALID</StatusTag> : null}
        {dupCodes.length ? (
          <StatusTag variant="err">DUPLICATE CODE: {dupCodes.join(", ")}</StatusTag>
        ) : null}
        <div className="flex-1" />
        {canWrite ? (
          <Button
            onClick={() => setSaveOpen(true)}
            disabled={!dirty || invalid > 0 || dupCodes.length > 0 || put.isPending}
          >
            <Save /> Save tiers
          </Button>
        ) : null}
      </div>
      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <aside className="grid content-start gap-2">
          <SortableList
            items={rows}
            getId={(r) => r.key}
            onReorder={(next) => update(() => next)}
            ariaLabel="Pricing tiers"
            rowClassName={(r, i) =>
              cn(
                r.key === selected && "border-primary ring-2 ring-ring/30",
                !results[i]?.success && "border-danger bg-danger-bg",
              )
            }
            renderItem={(r, i) => (
              <div className="flex items-center gap-1 py-1 pr-1">
                <button
                  type="button"
                  className="min-w-0 flex-1 py-1 text-left"
                  onClick={() => setSelected(r.key)}
                >
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="truncate font-medium">
                      {String(r.raw.name || r.raw.code || "New tier")}
                    </span>
                    {r.raw.highlight === true ? (
                      <StatusTag variant="gold">HIGHLIGHT</StatusTag>
                    ) : null}
                    {!results[i]?.success ? <StatusTag variant="err">INVALID</StatusTag> : null}
                  </div>
                  <div className="truncate font-mono text-muted-foreground text-xs">
                    {String(r.raw.code || "—")}
                  </div>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive"
                  aria-label={`Delete tier ${String(r.raw.code || i + 1)}`}
                  onClick={() => {
                    update((prev) => prev.filter((x) => x.key !== r.key));
                    if (selected === r.key) setSelected(null);
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            )}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const key = `new#${Date.now()}`;
              update((prev) => [...prev, { key, raw: newTier() }]);
              setSelected(key);
            }}
          >
            <Plus /> Add tier
          </Button>
        </aside>
        <section className="rounded-xl border bg-card p-4">
          {sel ? (
            <SchemaForm
              fields={TIER_FIELDS}
              value={sel.raw}
              omit={["sort"]}
              issues={selResult && !selResult.success ? selResult.error.issues : undefined}
              onChange={(raw) =>
                update((prev) => prev.map((r) => (r.key === sel.key ? { ...r, raw } : r)))
              }
              disabled={!canWrite}
            />
          ) : (
            <p className="text-muted-foreground text-sm">Select a tier to edit it, or add one.</p>
          )}
        </section>
      </div>
      <ReasonDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title="Save pricing tiers?"
        description="The public pricing page updates immediately; Stripe price ids are used by checkout."
        confirmLabel="Save"
        confirmVariant="default"
        minLength={0}
        reasonLabel="Note"
        onConfirm={onSave}
      />
    </>
  );
}
